import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, Lock, AlertCircle } from 'lucide-react'

import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
})

// Minimum length must match backend (see minPasswordLen in backend auth
// handler). Keeping it a plain const instead of a shared constants file —
// one-line rule, duplicating it is cheaper than a new import boundary.
const MIN_PASSWORD_LEN = 8

function ResetPasswordPage() {
  const navigate = useNavigate()

  // Read the token from the URL search string. We intentionally do NOT use
  // TanStack Router's validateSearch for this route: the token is opaque,
  // validated server-side, and typing it as a strict schema would only add
  // friction when we rename/rotate it later.
  const token = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('token') ?? ''
  }, [])

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const mutation = useMutation({
    mutationFn: async (payload: { token: string; newPassword: string }) =>
      apiClient.resetPassword(payload.token, payload.newPassword),
    onSuccess: () => {
      setDone(true)
      setError(null)
      // Small delay so the user sees the success UI, then auto-bounce to
      // login. Matches the dwell time of the login-success redirect in
      // /login.
      setTimeout(() => {
        navigate({ to: '/login' })
      }, 1600)
    },
    onError: (err: Error) => {
      setError(err.message || 'Could not reset password')
    },
  })

  // Missing token: almost certainly a user who opened the reset-password
  // URL manually, or an email client that stripped the query string. Show a
  // helpful message instead of a blank form.
  if (!token) {
    return (
      <AuthShell>
        <div className="space-y-5">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-rose-50 text-rose-600 ring-1 ring-rose-100 dark:bg-rose-950/50 dark:text-rose-300 dark:ring-rose-900">
            <AlertCircle className="h-6 w-6" />
          </div>
          <div>
            <h2 className="bhk-display text-[30px] font-extrabold leading-[1.1] tracking-[-0.015em]">Missing reset link</h2>
            <p className="mt-2 text-[15px] leading-relaxed text-[color:var(--bhk-muted)]">
              This page needs a reset token in the URL — make sure you opened the full link from the reset email (some
              email clients truncate long URLs).
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/forgot-password">
              <Button className="bhk-cta h-11 rounded-lg border-0 px-5 text-[14px] font-semibold">
                Request a new link
              </Button>
            </Link>
            <Link
              to="/login"
              className="bhk-link inline-flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--bhk-muted)] underline-offset-2 hover:text-[color:var(--bhk-cta)] hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        </div>
      </AuthShell>
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password.length < MIN_PASSWORD_LEN) {
      setError(`Password must be at least ${MIN_PASSWORD_LEN} characters`)
      return
    }
    if (password !== confirm) {
      setError("Passwords don't match")
      return
    }
    mutation.mutate({ token, newPassword: password })
  }

  return (
    <AuthShell>
      {done ? (
        <div className="space-y-5">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-900">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div>
            <h2 className="bhk-display text-[30px] font-extrabold leading-[1.1] tracking-[-0.015em]">Password updated</h2>
            <p className="mt-2 text-[15px] leading-relaxed text-[color:var(--bhk-muted)]">
              You can now sign in with your new password. Redirecting you to the login page…
            </p>
          </div>
          <Link to="/login">
            <Button className="bhk-cta h-11 rounded-lg border-0 px-5 text-[14px] font-semibold">
              Go to sign in
            </Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <h2 className="bhk-display text-[30px] font-extrabold leading-[1.1] tracking-[-0.015em]">Set a new password</h2>
            <p className="mt-2 text-[15px] leading-relaxed text-[color:var(--bhk-muted)]">
              Choose a password with at least {MIN_PASSWORD_LEN} characters. You'll use this to sign in from now on.
            </p>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-[14px] font-medium">
              New password
            </span>
            <div className="bhk-input flex h-12 items-center rounded-lg px-3.5">
              <Lock className="mr-2.5 h-4 w-4 text-[color:var(--bhk-muted)]" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-full border-0 bg-transparent px-0 text-[15px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                autoComplete="new-password"
                placeholder="••••••••"
                disabled={mutation.isPending}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="ml-1 grid h-8 w-8 place-items-center rounded-md text-[color:var(--bhk-muted)] hover:bg-[var(--bhk-hover)] hover:text-[color:var(--bhk-text)]"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[14px] font-medium">
              Confirm new password
            </span>
            <div className="bhk-input flex h-12 items-center rounded-lg px-3.5">
              <Lock className="mr-2.5 h-4 w-4 text-[color:var(--bhk-muted)]" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-full border-0 bg-transparent px-0 text-[15px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                autoComplete="new-password"
                placeholder="••••••••"
                disabled={mutation.isPending}
              />
            </div>
          </label>

          {error && (
            <div className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[13px] text-rose-800 dark:border-rose-900/70 dark:bg-rose-950/40 dark:text-rose-200">
              <span className="mt-0.5 grid h-4 w-4 flex-shrink-0 place-items-center rounded-full bg-rose-500 text-[10px] font-bold text-white">
                !
              </span>
              <div>
                <div className="font-semibold">Couldn't update password</div>
                <div className="text-rose-700 dark:text-rose-300">{error}</div>
              </div>
            </div>
          )}

          <Button
            type="submit"
            className="bhk-cta h-12 w-full rounded-lg border-0 text-[15px] font-semibold"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating password…
              </span>
            ) : (
              'Update password'
            )}
          </Button>

          <div className="pt-2">
            <Link
              to="/login"
              className="bhk-link inline-flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--bhk-muted)] underline-offset-2 hover:text-[color:var(--bhk-cta)] hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  )
}

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bhookly-login h-full w-full overflow-y-auto bg-[var(--bhk-surface)] text-[color:var(--bhk-text)]">
      <div className="mx-auto flex min-h-full max-w-[480px] flex-col justify-center px-6 py-10">
        <div className="bhk-display mb-10 flex items-baseline gap-1.5 text-xl leading-none">
          <span className="font-extrabold">Bhookly</span>
          <span className="font-normal opacity-80">Retail</span>
        </div>
        <div className="w-full">{children}</div>
      </div>
    </div>
  )
}
